import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { FEATURES } from '../config';
import {
  Search,
  Bell,
  Settings,
  User,
  Briefcase,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';

const Navbar: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <motion.nav
      className="h-20 bg-white/80 dark:bg-navy-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="h-full px-6 flex justify-between items-center">
        {/* Logo and Brand */}
        <div className="flex items-center space-x-4">
          <motion.div
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-glow-primary">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-display font-bold text-gradient">ManSearch</span>
          </motion.div>
          <span className="hidden lg:block text-[11px] text-slate-500 dark:text-slate-500 font-medium border-l border-slate-300 dark:border-slate-700 pl-4">
            Executive Job Search Agent
          </span>
        </div>

        {/* Search Bar — gated until wired to a backend (FEATURES.navbarSearch) */}
        {FEATURES.navbarSearch ? (
          <div className="flex-1 max-w-lg mx-8 hidden md:block">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                aria-label="Search jobs, companies, or skills"
                className="block w-full pl-11 pr-4 py-2.5 bg-slate-100 dark:bg-slate-800 border-none rounded-2xl text-sm placeholder-slate-400 dark:placeholder-slate-500 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all"
                placeholder="Search jobs, companies, or skills..."
              />
            </div>
          </div>
        ) : (
          <div className="flex-1" />
        )}

        {/* Right side */}
        <div className="flex items-center space-x-2">
          {/* Theme Toggle */}
          <motion.button
            aria-label="Toggle theme"
            className="p-2.5 rounded-xl text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
          >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </motion.button>

          {/* Notifications — gated until wired to a backend (FEATURES.navbarNotifications) */}
          {FEATURES.navbarNotifications && (
            <motion.button
              aria-label="Notifications"
              className="p-2.5 rounded-xl text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Bell className="h-5 w-5" />
            </motion.button>
          )}

          {/* Settings */}
          <motion.button
            aria-label="Settings"
            className="p-2.5 rounded-xl text-slate-400 hover:text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-5 w-5" />
          </motion.button>

          {/* Divider */}
          <div className="hidden md:block h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1" />

          {/* User Profile Dropdown */}
          <div className="relative">
            <motion.button
              type="button"
              aria-label="User menu"
              aria-expanded={showMenu}
              aria-haspopup="menu"
              className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer border-0 bg-transparent transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowMenu(!showMenu)}
            >
              <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center border-2 border-primary-500/20">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-slate-800 dark:text-white">{user?.fullName || 'User'}</p>
                <p className="text-[11px] text-slate-500 font-medium">{isAdmin ? 'Admin' : 'User'}</p>
              </div>
            </motion.button>

            {/* Dropdown Menu */}
            {showMenu && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-52 bg-white dark:bg-navy-800 rounded-xl shadow-large border border-slate-200 dark:border-slate-700 py-1 z-[60]"
                onMouseLeave={() => setShowMenu(false)}
              >
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.fullName}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <button
                  role="menuitem"
                  onClick={() => { navigate('/settings'); setShowMenu(false); }}
                  className="w-full flex items-center space-x-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-4 py-2.5 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
